import React, { useState, useEffect } from "react";
import PersonPinIcon from "@material-ui/icons/PersonPin";
import AccessTime from "@material-ui/icons/AccessTime";
import DevicesIcon from "@material-ui/icons/Devices";
import SecurityIcon from "@material-ui/icons/Security";
import { useFormState } from "react-final-form";
import {
  Datagrid,
  Edit,
  List,
  Toolbar,
  TabbedForm,
  FormTab,
  TextField,
  TextInput,
  SaveButton,
  useTranslate,
  useNotify,
  DateField,
  useQuery,
  Loading,
} from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import dataIntermediaryProvider from "../synapse/intermediaryDataProvider";
import SilentNotifications from "./custom/layout/SilentNotifications";
import SwitchGroup from "./custom/form/SwitchGroup";
import { capitalizeWords } from "../mixins/utils";
import ActivatedBooleanInput from "./ActivatedBooleanInput";
import UserListActions from "./UserListActions";
import { PERMISSIONS_LIST } from "../data/PermissionsList";

const useStyles = makeStyles(theme => ({
  center: {
    width: "fit-content",
    height: "fit-content",
    position: "absolute",
    zIndex: "99",
    top: "50%",
    left: "50%",
  },
  visible: {
    width: "100%",
  },
  blurred: {
    width: "100%",
    filter: "blur(10px)",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    width: "fit-content",
  },
  permissionsWrapper: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    margin: "1rem",
  },
  modalTitles: {
    fontFamily: theme.typography.fontFamily,
  },
}));

export const ROLES = {
  teacher: "LEHRER",
};

const updateUser = (
  id,
  isAdmin,
  isDeactivated,
  permissionsList,
  notify,
  lifter
) => {
  /*
   * id => number
   * isAdmin => boolean
   * isDeactivated => boolean
   * permissionsList => {permission: boolean}
   * notify => function useNotify hook
   * */
  lifter(true);

  // Workaround to send missing permissions to the BE (as they are not shown in the UI)
  if (permissionsList) {
    permissionsList.attachment_send = false;
  }

  dataIntermediaryProvider
    .updateUser(
      {
        admin: isAdmin,
        deactivated: isDeactivated,
        permissions: permissionsList,
      },
      id,
      "success_msg_deactivation"
    )
    .then(() => {
      lifter(false);
    })
    .catch(() => {
      lifter(false);
    });
};

const updateSilentTimes = (id, payload, notify, lifter) => {
  lifter(true);
  dataIntermediaryProvider
    .updateSingleSN(payload, id[0])
    .then(() => {
      lifter(false);
    })
    .catch(() => {
      lifter(false);
    });
};

const UserEditToolbar = props => {
  const notify = useNotify();
  const id = props.record.id;
  const { values } = useFormState();
  const isAdmin = values.admin;
  const isDeactivated = values.deactivated;
  const permissionsList = props.permissionsPayload;
  const currentTab = window.location.hash.split("/");
  const t = useTranslate();

  const setUpdateFunction = tab => {
    if (tab === "1") {
      return updateSilentTimes([id], props.snPayload, notify, props.lifter);
    }
    const permissions = permissionsList;

    return updateUser(
      id,
      isAdmin,
      isDeactivated,
      permissions,
      notify,
      props.lifter
    );
  };

  return (
    <Toolbar {...props}>
      <SaveButton
        onSave={() => {
          setUpdateFunction(currentTab[currentTab.length - 1]);
        }}
        disabled={props.isSnError || props.isSaveDisabled}
        submitOnEnter={true}
      />
    </Toolbar>
  );
};

const UserTitle = ({ record }) => {
  const translate = useTranslate();
  return (
    <span>
      {translate("resources.users.name", {
        smart_count: 1,
      })}{" "}
      {record ? `"${record.displayname}"` : ""}
    </span>
  );
};

export const UserEditComponent = props => {
  const classes = useStyles();
  const translate = useTranslate();
  const [payload, setPayload] = useState({});
  const [isSnError, setIsSnError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [singleUserPermissions, setSingleUserPermissions] = useState({});
  const [userPermissions, setUserPermissions] = useState(null);

  const setSNPayload = (payload, snError) => {
    setIsSnError(snError);
    setPayload(payload);
  };

  const permissionsInit = permissions => {
    return (
      PERMISSIONS_LIST.map((p, i) => ({
        id: i,
        name: p,
        checked: permissions[p],
        label: translate(`resources.roles_permissions.${p}`),
      })) || []
    );
  };

  const handleGroupPermissions = permList => {
    const updatedList = {};
    permList.forEach(perm => {
      updatedList[perm.name] = perm.checked;
    });
    setSingleUserPermissions(updatedList);
  };

  useEffect(() => {
    async function getPerms() {
      if (props.id) {
        await dataIntermediaryProvider
          .getOne("users", { id: props.id })
          .then(res => {
            const permissions = res.data.data.permission;
            setUserPermissions(permissions);
          });
      }
    }
    getPerms();
  }, [props.id]);

  const date_format = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const userId = props.id;
  const isTeacher = role => role?.toString().toUpperCase() === ROLES.teacher;
  const { data: userData } = useQuery({
    type: "getOne",
    resource: "users",
    payload: { id: userId },
  });
  const userRole = userData?.data?.role_slug;
  const currentTab = window.location.hash.split("/")[3];
  const isDevicesTabOpen = currentTab === "2";
  const permissions = userPermissions && permissionsInit(userPermissions);

  const handleLoadingStatus = val => {
    setIsLoading(val);
  };

  return (
    <Edit {...props} title={<UserTitle />}>
      <TabbedForm
        toolbar={
          <UserEditToolbar
            snPayload={payload}
            isSnError={isSnError}
            isSaveDisabled={!!isDevicesTabOpen}
            permissionsPayload={singleUserPermissions}
            lifter={handleLoadingStatus}
          />
        }
      >
        <FormTab
          label={translate("resources.users.name", { smart_count: 1 })}
          icon={<PersonPinIcon />}
        >
          {isLoading && (
            <Loading
              loadingPrimary=""
              loadingSecondary=""
              className={classes.center}
            />
          )}
          <div className={isLoading ? classes.blurred : classes.visible}>
            <div className={classes.column}>
              <TextInput
                source="username"
                disabled
                label={translate("resources.users.fields.uid")}
              />
              <TextInput
                source="name"
                disabled
                label={translate("resources.users.fields.name")}
                format={capitalizeWords}
              />
              <TextInput
                source="role_slug"
                disabled
                label={translate("resources.users.fields.role")}
                format={capitalizeWords}
              />
              <ActivatedBooleanInput
                source="deactivated"
                label={translate("resources.users.fields.activated")}
              />
            </div>
            <div className={classes.permissionsWrapper}>
              <div>
                <h2 className={classes.modalTitles}>
                  {translate("resources.roles_permissions.permissions")}
                </h2>
                {permissions && (
                  <SwitchGroup
                    {...props}
                    bulkLabel={translate(
                      "resources.roles_permissions.toggle_all"
                    )}
                    initialSwitchState={permissions}
                    break={true}
                    onValueChange={handleGroupPermissions}
                  />
                )}
              </div>
            </div>
          </div>
        </FormTab>
        <FormTab
          label={translate("synapseadmin.users.silent_times")}
          icon={<AccessTime />}
        >
          <div className={isLoading ? classes.blurred : classes.visible}>
            {isLoading && (
              <Loading
                loadingPrimary=""
                loadingSecondary=""
                className={classes.center}
              />
            )}
            <SilentNotifications {...props} lifter={setSNPayload} />
          </div>
        </FormTab>
        {isTeacher(userRole) && (
          <FormTab
            label={translate("resources.devices.name", { smart_count: 2 })}
            icon={<DevicesIcon />}
          >
            <List
              style={{ width: "100%" }}
              {...props}
              sort={{ field: "name", order: "ASC" }}
              actions={<UserListActions maxResults={5} />}
              bulkActionButtons={false}
              resource="devices"
              title=" "
            >
              <Datagrid style={{ width: "100%" }}>
                <TextField source="user_id" sortable={false} />
                <TextField source="device_id" sortable={false} />
                <TextField source="display_name" sortable={false} />
                <TextField source="last_seen_user_agent" sortable={false} />
                <TextField source="last_seen_ip" sortable={false} />
                <DateField
                  source="last_seen_ts"
                  showTime
                  options={date_format}
                  sortable={false}
                />
              </Datagrid>
            </List>
          </FormTab>
        )}
      </TabbedForm>
    </Edit>
  );
};

export default UserEditComponent;
